/**
 * Create wizard for the two heat-pump modules (bc_aqs / bc_aq). One
 * component, `kind`-parameterised — both share client + address; the
 * module-specific input is occupants (AQS) or area+phase+mode (AQ), with
 * a live sizing preview that mirrors the engine heuristics.
 *
 * Simpler than the solar wizard: no PVGIS, no map step. Lead search reuses
 * the Odoo lead search (OdooLeadSearch). On submit, posts to the module's
 * create action and navigates to the proposals list.
 */

import { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { ChevronRight, Loader2, Droplets, Flame } from "lucide-react"
import type { AppShell } from "@/App"
import { BC_AQ_DEFAULT_W_PER_M2, useModuleWPerM2 } from "@/lib/module-sizing"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select"
import {
  createBcAqs, createBcAq, getProposal, updateModuleProposal, listProducts,
  type OdooLead,
} from "@/lib/api"
import { OdooLeadSearch, clientFromOdooLead } from "@/components/OdooLeadSearch"
import { LeadOriginField } from "@/components/LeadOriginField"
import { isLeadOriginComplete, leadOriginFromRow, leadOriginPayload } from "@/lib/lead-origin"
import { detailPathForKind } from "@/lib/kind-labels"

type BcKind = "bc_aqs" | "bc_aq"

interface BcWizardProps {
  shell: AppShell | null
  kind: BcKind
}

interface FormState {
  lead_id: number | string | null
  /** Lead do CRM do Odoo escolhida na pesquisa (D1). */
  odoo_lead_id: number | null
  odoo_lead_name: string | null
  /** Origem da lead (obrigatória). */
  lead_origin: string | null
  lead_origin_detail: string
  client_name: string
  client_email: string
  client_phone: string
  address: string
  city: string
  postal_code: string
  // bc_aqs
  occupants: string
  // bc_aq
  heated_area_m2: string
  phase: "mono" | "tri"
  /** 'aq' = só aquecimento (depósito de inércia); 'aq_aqs' = + águas quentes. */
  mode: "aq" | "aq_aqs"
  /** Depósito: desligar quando o cliente já tem um instalado. */
  include_tank: boolean
  /** Nível dos acessórios de instalação (1–3 no AQ, 1–4 no AQS). */
  install_level: string
  /** Marca da bomba (filtro estrito no motor). Vazio = qualquer marca. */
  brand: string
  notes: string
}

const EMPTY: FormState = {
  lead_id: null, odoo_lead_id: null, odoo_lead_name: null, lead_origin: null, lead_origin_detail: "", client_name: "", client_email: "", client_phone: "",
  address: "", city: "", postal_code: "",
  occupants: "2", heated_area_m2: "100", phase: "mono", mode: "aq",
  include_tank: true, install_level: "1", brand: "",
  notes: "",
}

/** Opção "qualquer marca" — o Select não aceita value="". */
const ANY_BRAND = "__any__"

/** Horas de mão de obra por nível (totais, 2 técnicos) — espelha o motor. */
const AQ_LABOUR_HOURS: Record<string, number> = { "1": 16, "2": 24, "3": 36 }
const AQS_LABOUR_HOURS = 6

const META: Record<BcKind, { title: string; Icon: typeof Droplets }> = {
  bc_aqs: { title: "Bomba Calor — Águas Quentes (AQS)", Icon: Droplets },
  bc_aq: { title: "Bomba Calor — Aquecimento (AQ)", Icon: Flame },
}

// Mirror of engine sizing so the user sees the suggestion before submit.
function tankPreview(occupants: number): number {
  if (occupants <= 2) return 200
  if (occupants <= 4) return 260
  return 300
}
function kwPreview(area: number, wPerM2: number): number {
  return Math.max(4, Math.round((area * wPerM2) / 1000 * 10) / 10)
}

export function BcWizard({ shell, kind }: BcWizardProps) {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)
  const [form, setForm] = useState<FormState>(EMPTY)
  // W/m² das Configurações (Bomba Calor Aquec.) — o motor usa o mesmo valor.
  const wPerM2 = useModuleWPerM2(shell, "bc_aq_config", BC_AQ_DEFAULT_W_PER_M2)
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(isEdit)
  const { title, Icon } = META[kind]

  const update = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  // Edit mode: load the existing proposal and prefill.
  useEffect(() => {
    if (!shell || !id) return
    setLoading(true)
    getProposal(shell, id)
      .then((raw) => {
        const p = raw as Record<string, unknown>
        const str = (v: unknown) => (v == null ? "" : String(v))
        setForm({
          lead_id: (p.lead_id as number | string | null) ?? null,
          odoo_lead_id: Number(p.odoo_lead_id) > 0 ? Number(p.odoo_lead_id) : null,
          odoo_lead_name: (p.odoo_lead_name as string | null) ?? null,
          ...leadOriginFromRow(p),
          client_name: str(p.client_name),
          client_email: str(p.client_email),
          client_phone: str(p.client_phone),
          address: str(p.address),
          city: str(p.city),
          postal_code: str(p.postal_code),
          occupants: str(p.occupants) || "2",
          heated_area_m2: str(p.heated_area_m2) || "100",
          phase: p.phase === "tri" ? "tri" : "mono",
          mode: p.mode === "aq_aqs" ? "aq_aqs" : "aq",
          include_tank: p.include_tank !== false,
          install_level: str(p.install_level) || "1",
          brand: str(p.brand),
          notes: str(p.notes),
        })
      })
      .catch((e) => shell.bridge.toast({ variant: "error", description: e instanceof Error ? e.message : "Falha a carregar" }))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shell, id])

  // Marcas disponíveis = marcas distintas das bombas do módulo no catálogo.
  const [brands, setBrands] = useState<string[]>([])
  useEffect(() => {
    if (!shell) return
    let cancelled = false
    listProducts(shell, { kind, metadata_role: "heat_pump", limit: 200 })
      .then((items) => {
        if (cancelled) return
        const uniq = Array.from(
          new Set(items.map((i) => (i.brand_name ?? "").trim()).filter(Boolean)),
        ).sort()
        setBrands(uniq)
      })
      .catch(() => {
        if (!cancelled) setBrands([])
      })
    return () => {
      cancelled = true
    }
  }, [shell, kind])

  const preview = useMemo(() => {
    if (kind === "bc_aqs") {
      const occ = Number(form.occupants) || 0
      return occ > 0 ? `Depósito sugerido: ${tankPreview(occ)} L` : null
    }
    const area = Number(form.heated_area_m2) || 0
    return area > 0 ? `Potência sugerida: ~${kwPreview(area, wPerM2)} kW (${wPerM2} W/m²)` : null
  }, [kind, form.occupants, form.heated_area_m2])

  const canSubmit =
    form.client_name.trim().length > 0 &&
    isLeadOriginComplete(form) &&
    (kind === "bc_aqs" ? Number(form.occupants) >= 1 : Number(form.heated_area_m2) > 0) &&
    !submitting

  async function submit() {
    if (!shell || !canSubmit) return
    setSubmitting(true)
    try {
      const common = {
        lead_id: form.lead_id,
        odoo_lead_id: form.odoo_lead_id,
        odoo_lead_name: form.odoo_lead_name,
        ...leadOriginPayload(form),
        client_name: form.client_name.trim(),
        client_email: form.client_email.trim() || null,
        client_phone: form.client_phone.trim() || null,
        address: form.address.trim() || null,
        city: form.city.trim() || null,
        postal_code: form.postal_code.trim() || null,
      }
      const brand = form.brand.trim() || null
      const moduleInputs = kind === "bc_aqs"
        ? { ...common, occupants: Number(form.occupants), install_level: Number(form.install_level), brand }
        : {
            ...common,
            heated_area_m2: Number(form.heated_area_m2),
            phase: form.phase,
            mode: form.mode,
            include_tank: form.include_tank,
            install_level: Number(form.install_level),
            brand,
          }
      const res = isEdit
        ? await updateModuleProposal(shell, id!, moduleInputs)
        : kind === "bc_aqs"
          ? await createBcAqs(shell, moduleInputs as Parameters<typeof createBcAqs>[1])
          : await createBcAq(shell, moduleInputs as Parameters<typeof createBcAq>[1])
      shell.bridge.toast({ variant: "success", description: `Orçamento ${res.number} ${isEdit ? "guardado" : "criado"}` })
      navigate(isEdit ? (detailPathForKind(kind, id!) ?? "/list") : "/list")
    } catch (err) {
      shell.bridge.toast({
        variant: "error",
        description: err instanceof Error ? err.message : "Falha a criar orçamento",
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <p className="text-sm text-muted-foreground">A carregar…</p>

  return (
    <div className="space-y-6">
      <div>
        <nav className="flex items-center gap-1 text-xs text-muted-foreground">
          <button onClick={() => navigate("/simulador")} className="hover:underline">Início</button>
          <ChevronRight className="h-3 w-3" />
          <span>{isEdit ? "Editar orçamento" : "Novo orçamento"}</span>
        </nav>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Icon className="h-6 w-6 text-accent" />
          {title}
        </h1>
      </div>

      <LeadClientCard shell={shell} kind={kind} form={form} update={update} />

      <Card>
        <CardHeader>
          <CardTitle>Dimensionamento</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="max-w-xs space-y-1">
            <Label>Marca</Label>
            <Select
              value={form.brand || ANY_BRAND}
              onValueChange={(v) => update("brand", v === ANY_BRAND ? "" : v)}
              disabled={brands.length === 0}
            >
              <SelectTrigger>
                <SelectValue placeholder={brands.length ? "Qualquer marca" : "A carregar…"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY_BRAND}>Qualquer marca</SelectItem>
                {brands.map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Com marca escolhida, o simulador só usa equipamentos dessa marca.
            </p>
          </div>

          {kind === "bc_aqs" ? (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <Label required>Nº de ocupantes</Label>
                <Input
                  type="number" min={1} step={1}
                  value={form.occupants}
                  onChange={(e) => update("occupants", e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label>Nível de instalação</Label>
                <Select value={form.install_level} onValueChange={(v) => update("install_level", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Nível 1 — até 10 m</SelectItem>
                    <SelectItem value="2">Nível 2 — até 15 m</SelectItem>
                    <SelectItem value="3">Nível 3 — até 20 m</SelectItem>
                    <SelectItem value="4">Nível 4 — entre 20 e 30 m</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Mão de obra: {AQS_LABOUR_HOURS}h por defeito, editáveis no Editar Kit.
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="space-y-1">
                <Label required>Área a aquecer (m²)</Label>
                <Input
                  type="number" min={1} step={1}
                  value={form.heated_area_m2}
                  onChange={(e) => update("heated_area_m2", e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label>Fase</Label>
                <Select value={form.phase} onValueChange={(v) => update("phase", v as "mono" | "tri")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mono">Monofásica</SelectItem>
                    <SelectItem value="tri">Trifásica</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Modo</Label>
                <Select value={form.mode} onValueChange={(v) => update("mode", v as FormState["mode"])}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aq">Só aquecimento (depósito de inércia)</SelectItem>
                    <SelectItem value="aq_aqs">Aquecimento + águas quentes (termoacumulador)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Nível de instalação</Label>
                <Select value={form.install_level} onValueChange={(v) => update("install_level", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Nível 1 — até 10 m</SelectItem>
                    <SelectItem value="2">Nível 2 — até 15 m</SelectItem>
                    <SelectItem value="3">Nível 3 — até 20 m</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Mão de obra: {AQ_LABOUR_HOURS[form.install_level] ?? 16}h por defeito, editáveis no Editar Kit.
                </p>
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label>Depósito</Label>
                <label className="flex h-9 items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={form.include_tank}
                    onChange={(e) => update("include_tank", e.target.checked)}
                  />
                  Incluir {form.mode === "aq_aqs" ? "termoacumulador" : "depósito de inércia"} na proposta
                </label>
                <p className="text-xs text-muted-foreground">
                  Desliga quando o cliente já tem depósito instalado.
                </p>
              </div>
            </div>
          )}
          {preview && (
            <div className="rounded-md border border-accent/40 bg-accent/10 px-3 py-2 text-sm font-medium text-accent">
              {preview}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate(isEdit ? (detailPathForKind(kind, id!) ?? "/list") : "/")}>Cancelar</Button>
        <Button onClick={submit} disabled={!canSubmit}>
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {isEdit ? "Guardar alterações" : "Criar orçamento"}
        </Button>
      </div>
    </div>
  )
}

// ── Client card with lead search (shared shape with solar wizard) ────────

function LeadClientCard({
  shell, kind, form, update,
}: {
  shell: AppShell | null
  kind: BcKind
  form: FormState
  update: <K extends keyof FormState>(k: K, v: FormState[K]) => void
}) {
  function selectLead(lead: OdooLead) {
    const c = clientFromOdooLead(lead)
    update("odoo_lead_id", lead.id)
    update("odoo_lead_name", lead.name)
    update("client_name", c.client_name)
    update("client_email", c.client_email)
    update("client_phone", c.client_phone)
    update("address", c.address)
    update("city", c.city)
    update("postal_code", c.postal_code)
  }

  return (
    <Card>
      <CardHeader><CardTitle>Cliente</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <OdooLeadSearch
          shell={shell}
          module={kind}
          linked={form.odoo_lead_id ? { id: form.odoo_lead_id, name: form.odoo_lead_name } : null}
          onSelect={selectLead}
          onClear={() => { update("odoo_lead_id", null); update("odoo_lead_name", null) }}
        />

        <LeadOriginField
          value={{ lead_origin: form.lead_origin, lead_origin_detail: form.lead_origin_detail }}
          onChange={(o) => { update("lead_origin", o.lead_origin); update("lead_origin_detail", o.lead_origin_detail) }}
        />

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label required>Nome</Label>
            <Input value={form.client_name} onChange={(e) => update("client_name", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Email</Label>
            <Input type="email" value={form.client_email} onChange={(e) => update("client_email", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Telefone</Label>
            <Input value={form.client_phone} onChange={(e) => update("client_phone", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Morada</Label>
            <Input value={form.address} onChange={(e) => update("address", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Cidade</Label>
            <Input value={form.city} onChange={(e) => update("city", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Código postal</Label>
            <Input value={form.postal_code} onChange={(e) => update("postal_code", e.target.value)} />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
